import React, { useState, useEffect } from "react";
import NavHeader from "../components/nav/NavHeader";
import SideMenu from "../components/nav/Sidemenu";
import RoleManagementTable from "../components/rolesRegistrations/RoleManagementTable";
import RoleRegistrationsForm from "../components/rolesRegistrations/RoleRegistrationsForm";
import "../assets/css/reportRegistrations.css";
import PageHeader from "../components/layout/PageHeader";
import TabStrip from "../components/layout/TabStrip";
import RankedTable from "../components/layout/RankedTable";
import SingleFieldEditModal from "../components/layout/SingleFieldEditModal";
import ConfirmDialog from "../components/layout/ConfirmDialog";
import AppButton from "../components/layout/AppButton";
import ToastNotification from "../components/layout/ToastNotification";
import { useRoleAdmin } from "../hooks/useRoleAdmin";
import { FaShieldAlt, FaPlusCircle } from "react-icons/fa";

const RolesAdministration = () => {
    const {
        fetchRoles: fetchRawRoles,
        createRole: createRoleApi,
        updateRole: updateRoleApi,
        deleteRole: deleteRoleApi,
    } = useRoleAdmin();
    const [activeTab, setActiveTab] = useState("management");
    const [searchText, setSearchText] = useState("");
    const [roles, setRoles] = useState([]);
    const [tableLoading, setTableLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editRoleData, setEditRoleData] = useState(null);
    const [createLoading, setCreateLoading] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, roleId: null });
    const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

    const showToast = (message, severity = "success") => setToast({ open: true, message, severity });
    const handleCloseToast = () => setToast((t) => ({ ...t, open: false }));

    const formatDateFromISO = (isoString) => {
        if (!isoString) return "";
        const date = new Date(isoString);
        const pad = (v) => String(v).padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    const loadRoles = async () => {
        setTableLoading(true);
        try {
            const data = await fetchRawRoles();
            const raw = Array.isArray(data) ? data : [];
            setRoles(raw.map((r) => ({
                id: r.roleId,
                name: r.roleName,
                createdBy: r.createdBy || "",
                createdAt: formatDateFromISO(r.createdAt),
            })));
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setTableLoading(false);
        }
    };

    useEffect(() => {
        loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredRoles = roles.filter((r) =>
        r.name.toLowerCase().includes(searchText.toLowerCase())
    );

    // ── create ────────────────────────────────────────────────────────────────
    const handleCreateRole = async (name) => {
        const isDuplicate = roles.some((r) => r.name.toLowerCase() === name.toLowerCase());
        if (isDuplicate) {
            showToast("A role with this name already exists.", "warning");
            return;
        }
        try {
            setCreateLoading(true);
            const result = await createRoleApi({ roleName: name });
            await loadRoles();
            showToast(result?.message || "Role created successfully");
            setActiveTab("management");
        } catch (err) {
            showToast("Error creating role: " + err.message, "error");
        } finally {
            setCreateLoading(false);
        }
    };

    // ── edit ──────────────────────────────────────────────────────────────────
    const handleEditRole = (role) => {
        setEditRoleData({ ...role });
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditRoleData(null);
    };

    const handleUpdateRole = async (event) => {
        event.preventDefault();
        if (!editRoleData) return;
        try {
            setUpdateLoading(true);
            const result = await updateRoleApi(editRoleData.id, { roleName: editRoleData.name });
            await loadRoles();
            showToast(result?.message || "Role updated successfully");
            handleCloseEditModal();
        } catch (err) {
            showToast("Error updating role: " + err.message, "error");
        } finally {
            setUpdateLoading(false);
        }
    };

    // ── delete ────────────────────────────────────────────────────────────────
    const handleDeleteRole = (id) => {
        if (!id) { showToast("Invalid role ID", "error"); return; }
        setConfirmDialog({ open: true, roleId: id });
    };

    const handleConfirmDeleteRole = async () => {
        const id = confirmDialog.roleId;
        if (!id) return;
        try {
            setDeletingId(id);
            const result = await deleteRoleApi(id);
            await loadRoles();
            showToast(result?.message || "Role deleted successfully");
            setConfirmDialog({ open: false, roleId: null });
        } catch (err) {
            showToast("Error deleting role: " + err.message, "error");
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div>
            <NavHeader />
            <SideMenu />
            <div style={{ position: "relative" }} id="main">
                <PageHeader
                    title="RLS Management"
                    subtitle="Manage and register RLS for your application."
                />

                <div className="pb-4">
                    <TabStrip
                        tabs={[
                            { label: "RLS Management", value: "management", icon: <FaShieldAlt /> },
                            { label: "RLS Registration", value: "registrations", icon: <FaPlusCircle /> },
                        ]}
                        active={activeTab}
                        onChange={setActiveTab}
                    />
                </div>

                {error && (
                    <div className="container-fluid pb-4">
                        <div className="alert alert-warning alert-dismissible fade show" role="alert">
                            <strong>Warning:</strong> {error}
                            <AppButton variant="outline" onClick={() => setError(null)} style={{ padding: "2px 10px", float: "right", lineHeight: 1 }} title="Dismiss">
                                ×
                            </AppButton>
                        </div>
                    </div>
                )}

                {tableLoading && activeTab === "management" ? (
                    <div className="pb-4">
                        <RankedTable
                            columns={[{ label: "RLS Name" }, { label: "Created At" }, { label: "Actions", align: "center" }]}
                            rows={Array.from({ length: 6 }, () => ({ _skeleton: true }))}
                            emptyText=""
                            renderRow={() => (<>
                                {[1,2,3].map(j => (
                                    <td key={j} className="px-5 py-3">
                                        <div style={{ height: "13px", background: "#f1f5f9", borderRadius: "6px", width: j === 3 ? "60%" : "85%", animation: "pulse 1.5s ease-in-out infinite" }} />
                                    </td>
                                ))}
                            </>)}
                        />
                    </div>
                ) : activeTab === "management" ? (
                    <RoleManagementTable
                        searchText={searchText}
                        setSearchText={setSearchText}
                        filteredRoles={filteredRoles}
                        onEditRole={handleEditRole}
                        onDeleteRole={handleDeleteRole}
                        deleteLoadingId={deletingId}
                    />
                ) : (
                    <RoleRegistrationsForm onCreateRole={handleCreateRole} loading={createLoading} />
                )}

                <SingleFieldEditModal
                    open={showEditModal && !!editRoleData}
                    title="Edit Role"
                    label="Role Name"
                    value={editRoleData?.name || ""}
                    placeholder="Enter role name"
                    onChange={(e) => setEditRoleData(prev => ({ ...prev, name: e.target.value }))}
                    onSubmit={handleUpdateRole}
                    onClose={handleCloseEditModal}
                    loading={updateLoading}
                />
                <ConfirmDialog
                    open={confirmDialog.open}
                    title="Delete Role"
                    message="Are you sure you want to delete this role? This action cannot be undone."
                    confirmLabel="Delete"
                    loadingLabel="Deleting..."
                    loading={!!deletingId}
                    onConfirm={handleConfirmDeleteRole}
                    onClose={() => { if (!deletingId) setConfirmDialog({ open: false, roleId: null }); }}
                />
                <ToastNotification
                    open={toast.open}
                    message={toast.message}
                    severity={toast.severity}
                    onClose={handleCloseToast}
                />
            </div>
        </div>
    );
};

export default RolesAdministration;
